'use client';

/**
 * TacticalDashboard — the bespoke body for the "Tactical" persona.
 *
 * A SOF-style readiness console. The framing: give the operator
 * everything they need to make themselves more dangerous, organized
 * into the three readiness domains the military actually uses —
 * PHYSICAL, MENTAL, SPIRITUAL (Army H2F / Spiritual Readiness). Each
 * pillar is a force-multiplier readout: a status, a number, and a
 * supporting line, drilling into the deeper metric.
 *
 * Layout:
 *   1. DEPLOYMENT STATUS — overall GO / CAUTION / STAND DOWN, the
 *      composite of the three pillars.
 *   2. The 3-pillar console (Physical / Mental / Spiritual).
 *   3. Deploy — the workout CTA (shared StartWorkoutCard).
 *
 * Pillar sourcing:
 *   PHYSICAL  — readiness score + operator-score capability (strength/
 *               endurance/recovery). Body's state + capability.
 *   MENTAL    — today's subjective check-in (energy + motivation +
 *               inverted stress). Mind in the fight.
 *   SPIRITUAL — active pillar challenge's spiritual completion + the
 *               why-statement. Purpose intact. Degrades gracefully to
 *               a "set your why" prompt when no challenge is active.
 *
 * No gamification, operations, or territory — this persona opens the
 * app for truth from data, not a game.
 */

import { useEffect, useState } from 'react';
import { Activity, Brain, Compass, ArrowRight } from 'lucide-react';
import { displayReadiness } from '@/lib/unifiedIntelligence';
import StartWorkoutCard from '@/components/dashboard/StartWorkoutCard';
import ActionPoints from '@/components/metrics-briefing/ActionPoints';
import FormCurveCard from '@/components/metrics-ops/FormCurveCard';
import VolumeByMuscleCard from '@/components/metrics-ops/VolumeByMuscleCard';
import type { FormCurveSummary } from '@/lib/formCurve';
import type { VolumeByMuscleSummary } from '@/lib/volumeByMuscle';
import type { DashboardData } from '@/lib/hooks/useDashboardData';
import { useChallenge } from '@/lib/hooks/useChallenge';

type PillarStatus = 'strong' | 'steady' | 'degraded' | 'unknown';

interface PillarReadout {
  label: string;
  icon: typeof Activity;
  score: number | null;        // 0-100, null = no data
  status: PillarStatus;
  statusWord: string;
  detail: string;
  href: string;
}

const STATUS_TW: Record<PillarStatus, { tw: string; bar: string; border: string }> = {
  strong:   { tw: 'text-emerald-400', bar: 'bg-emerald-400', border: 'border-emerald-500/40' },
  steady:   { tw: 'text-amber-400',   bar: 'bg-amber-400',   border: 'border-amber-500/40' },
  degraded: { tw: 'text-red-400',     bar: 'bg-red-400',     border: 'border-red-500/40' },
  unknown:  { tw: 'text-slate-500',   bar: 'bg-slate-600',   border: 'border-slate-700' },
};

function classify(score: number | null, words: [string, string, string]): { status: PillarStatus; word: string } {
  if (score == null) return { status: 'unknown', word: 'NO DATA' };
  if (score >= 70) return { status: 'strong', word: words[0] };
  if (score >= 40) return { status: 'steady', word: words[1] };
  return { status: 'degraded', word: words[2] };
}

export default function TacticalDashboard({ data }: { data: DashboardData }) {
  const { activeBreak } = data;
  const ir = data.intelligenceReport;
  const cs = ir?.currentState;

  // SPIRITUAL — active challenge spiritual completion (preferred), with a
  // training-streak "discipline" fallback so the pillar isn't permanently
  // dead for operators who haven't started a pillar challenge.
  const [spiritual, setSpiritual] = useState<{ score: number | null; detail: string }>({ score: null, detail: 'Set your why to anchor the spiritual pillar' });
  // Force-multiplier deep panel data — Form curve (load/durability) +
  // Volume-by-muscle (capacity). Action Points self-loads via its own
  // component. Loaded once on mount.
  const [formCurve, setFormCurve] = useState<FormCurveSummary | null>(null);
  const [volume, setVolume] = useState<VolumeByMuscleSummary | null>(null);
  const [deepLoaded, setDeepLoaded] = useState(false);

  // Spiritual pillar reads the SHARED challenge snapshot — no second
  // getActiveChallenge, and critically no second `evaluateToday` (that is
  // a write; the dashboard used to fire one on every mount of this
  // persona, racing the mission card's own pass). The hook has already
  // evaluated today for an active arc.
  const { challenge: chal, status: chalStatus, loading: chalLoading } = useChallenge();

  useEffect(() => {
    if (!data.user?.uid || chalLoading) return;
    let cancelled = false;

    // Explicit discipline signal: an ACTIVE arc with spiritual commitments.
    const sp = chal && chal.status === 'active' ? chalStatus?.pillarCounts.spiritual : undefined;
    if (sp && sp.total > 0) {
      const pct = Math.round((sp.done / sp.total) * 100);
      setSpiritual({
        score: pct,
        detail: `${sp.done}/${sp.total} spiritual commitments today · ${chal?.why?.text ? '"' + chal.why.text + '"' : 'discipline holding'}`,
      });
      return;
    }

    // Fallback: training-streak as a discipline proxy. Score maps
    // streak days onto 0-100 via a soft curve — 1 day = 30, 7 = 70,
    // 30 = 95, 60+ caps at 100. Honest about the substitution.
    (async () => {
      try {
        const { loadProgressionState } = await import('@/lib/progressionService');
        const ps = await loadProgressionState(data.user!.uid);
        if (cancelled) return;
        const streakDays = ps?.streakDays || 0;
        if (streakDays > 0) {
          const score = Math.min(100, Math.round(30 + 40 * Math.log10(streakDays + 1)));
          const noun = streakDays === 1 ? 'day' : 'days';
          setSpiritual({ score, detail: `Discipline proxy — ${streakDays}-${noun} training streak · start a pillar challenge for the full signal` });
        } else {
          setSpiritual({ score: null, detail: 'Start a pillar challenge — or break the seal with one training day' });
        }
      } catch {
        if (!cancelled) setSpiritual({ score: null, detail: 'Spiritual pillar unavailable' });
      }
    })();

    return () => { cancelled = true; };
  }, [data.user?.uid, chalLoading, chal, chalStatus]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!data.user?.uid) return;
      // Deep panel — form curve + volume by muscle.
      try {
        const [{ getSessionHistory }, { computeFormCurve }, { computeVolumeByMuscle }] = await Promise.all([
          import('@/lib/sessionStorage'),
          import('@/lib/formCurve'),
          import('@/lib/volumeByMuscle'),
        ]);
        const sessions = await getSessionHistory({ limit: 200 });
        if (cancelled) return;
        setFormCurve(computeFormCurve(sessions, 84));
        setVolume(computeVolumeByMuscle(sessions, 7));
      } catch (e) {
        console.warn('[tactical] deep panel load failed:', e);
      } finally {
        if (!cancelled) setDeepLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [data.user?.uid]);

  // ── PHYSICAL ──
  // See displayReadiness — null when the number is the provisional constant.
  const physScore = displayReadiness(data.intelligenceReport);
  const physClass = classify(physScore, ['PRIMED', 'SERVICEABLE', 'DEGRADED']);
  const cap = cs?.operatorScoreBreakdown;
  const ta = data.todayActivity;
  // Append today's HRV + sleep when available — the recovery vitals an
  // SOF user reads first thing. Falls back gracefully when the wearable
  // hasn't synced.
  const vitals: string[] = [];
  if (ta?.hrv && ta.hrv > 0) vitals.push(`HRV ${Math.round(ta.hrv)}`);
  if (ta?.sleepDurationMinutes && ta.sleepDurationMinutes > 0) {
    vitals.push(`sleep ${(ta.sleepDurationMinutes / 60).toFixed(1)}h`);
  }
  const baseCapLine = cap
    ? `Capability ${Math.round(cap.strength + cap.endurance)}/55 · recovery ${cap.recovery}/15`
    : 'Log training to build capability signal';
  const capLine = vitals.length > 0 ? `${baseCapLine} · ${vitals.join(' · ')}` : baseCapLine;

  // ── MENTAL ── composite of energy + motivation + inverted stress.
  const subj = cs?.todaySubjective;
  const mentalScore = subj
    ? Math.round(((subj.energy + subj.motivation + (10 - subj.stress)) / 30) * 100)
    : null;
  const mentalClass = classify(mentalScore, ['SHARP', 'STEADY', 'FRAYED']);
  const mentalLine = subj
    ? `Energy ${subj.energy}/10 · motivation ${subj.motivation}/10 · stress ${subj.stress}/10`
    : 'Log a check-in to read mental readiness';

  // ── SPIRITUAL ──
  const spiritClass = classify(spiritual.score, ['ANCHORED', 'HOLDING', 'DRIFTING']);

  const pillars: PillarReadout[] = [
    { label: 'Physical', icon: Activity, score: physScore, status: physClass.status, statusWord: physClass.word, detail: capLine, href: '/recovery' },
    { label: 'Mental', icon: Brain, score: mentalScore, status: mentalClass.status, statusWord: mentalClass.word, detail: mentalLine, href: '/recovery' },
    { label: 'Spiritual', icon: Compass, score: spiritual.score, status: spiritClass.status, statusWord: spiritClass.word, detail: spiritual.detail, href: '/challenge' },
  ];

  // Deployment status = the TRAINING-readiness call. It is PHYSICAL-led:
  // whether you can train hard today depends on recovery + capability, not
  // on whether today's spiritual/mental check-ins are done yet. Mental,
  // when logged and frayed, caps an otherwise-GO call down to CAUTION.
  // Spiritual is a discipline pillar — shown below, but it never gates
  // training. (Previously a pure Math.min across all three let a 0
  // spiritual force STAND DOWN on a fully-rested, primed operator.)
  let composite: number | null = physScore;
  if (composite != null && mentalScore != null && mentalScore < 40) {
    composite = Math.min(composite, 55); // mentally fried → cap at CAUTION
  }
  const deployClass = classify(composite, ['GO', 'CAUTION', 'STAND DOWN']);
  const dc = STATUS_TW[deployClass.status];

  return (
    <div className="space-y-3">
      {/* DEPLOYMENT STATUS — overall readiness call */}
      <section className={`relative overflow-hidden rounded-xl border ${dc.border} bg-hud-ink/85`}>
        <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-current to-transparent opacity-30" />
        <header className="px-4 py-1.5 border-b border-slate-800/60 font-mono text-[9px] uppercase tracking-[0.28em] text-slate-500">
          Tactical Readiness Console · SYS·TRC·01
        </header>
        <div className="px-4 py-3 flex items-center gap-4">
          <span className={`w-3 h-3 rounded-full ${dc.bar} animate-hud-blink shrink-0`} aria-hidden />
          <div className="min-w-0 flex-1">
            <div className={`font-heading text-2xl font-bold tracking-wide ${dc.tw}`}>{deployClass.word}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400 mt-0.5">
              Deployment status · led by physical readiness
            </div>
          </div>
          {composite != null && (
            <div className="text-right shrink-0">
              <div className={`font-heading text-3xl font-bold tabular-nums ${dc.tw}`}>{composite}</div>
              <div className="font-mono text-[8px] uppercase tracking-[0.18em] text-slate-600">readiness</div>
            </div>
          )}
        </div>
      </section>

      {/* 3-PILLAR CONSOLE */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {pillars.map(p => {
          const t = STATUS_TW[p.status];
          const Icon = p.icon;
          return (
            <button
              key={p.label}
              onClick={() => { window.location.href = p.href; }}
              className={`text-left rounded-xl border ${t.border} bg-hud-ink/70 p-3 hover:bg-hud-ink/90 transition active:scale-[0.99]`}
            >
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-1.5">
                  <Icon className={`w-3.5 h-3.5 ${t.tw}`} />
                  <span className="font-mono text-[9px] uppercase tracking-[0.22em] font-bold text-slate-300">{p.label}</span>
                </div>
                <ArrowRight className="w-3 h-3 text-slate-600" />
              </div>
              <div className="flex items-baseline gap-2">
                <span className={`font-heading text-xl font-bold tabular-nums ${t.tw}`}>
                  {p.score != null ? p.score : '—'}
                </span>
                <span className={`font-mono text-[9px] uppercase tracking-[0.18em] font-bold ${t.tw}`}>{p.statusWord}</span>
              </div>
              {/* mini bar */}
              <div className="relative h-1 rounded-sm bg-slate-900 border border-slate-800 overflow-hidden mt-1.5">
                <div className={`absolute inset-y-0 left-0 ${t.bar}`} style={{ width: `${p.score ?? 0}%` }} />
              </div>
              <p className="text-[10px] text-slate-500 leading-snug mt-1.5">{p.detail}</p>
            </button>
          );
        })}
      </div>

      {/* DEPLOY — the workout CTA */}
      {/* "Start Workout", not "Deploy" — plain English in buttons. */}
      {!activeBreak && <StartWorkoutCard data={data} label="Start Workout" />}

      {/* ── FORCE MULTIPLIER ── the deep instrument panel. The pillars
          summarize; these prove it and tell the operator what to do. */}
      <div className="pt-1">
        <div className="font-mono text-[9px] uppercase tracking-[0.28em] text-slate-500 px-1 mb-1.5">
          Force Multiplier
        </div>
        <div className="space-y-2.5">
          {/* Today's orders — the cross-domain action feed. Self-loads. */}
          {data.user?.uid && <ActionPoints userId={data.user.uid} />}
          {/* Durability / load — fitness vs fatigue vs form. */}
          {deepLoaded && <FormCurveCard summary={formCurve} />}
          {/* Capacity — weekly volume per muscle vs productive range. */}
          {deepLoaded && <VolumeByMuscleCard summary={volume} />}
        </div>
      </div>
    </div>
  );
}
